import styled from "styled-components";

import { InfoIcon } from "../../icons/glyphs";
import { AnchoredPopup, useAnchoredPopupAnchor } from "../AnchoredPopup";
import { IconButton } from "../IconButton";
import { Text, defaultTextVariants } from "../Text";
import { Tooltip, useTooltipController } from "../Tooltip";
import { AnnotationPointMarkerGlyph } from "./AnnotationPointMarkerGlyph";
import { resolveHistoryShortcutModifier } from "./annotationShortcuts";
import { annotationStudioText } from "./messages";
import type { AnnotationStudioLocale } from "./types";

const HelpTitle = styled(Text)`
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`;

const HelpSections = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const HelpSection = styled.section`
  display: grid;
  gap: 0.45rem;
`;

const HelpSectionTitle = styled(Text)`
  color: ${({ theme }) => theme.color.text.default};
`;

const HelpList = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const HelpItem = styled.div`
  display: grid;
  grid-template-columns: 6.75rem minmax(0, 1fr);
  align-items: center;
  gap: 0.65rem;
  color: ${({ theme }) => theme.color.text.subtle};
  font-size: ${({ theme }) => theme.typography.size.caption};
`;

const ShortcutKey = styled.kbd`
  justify-self: start;
  padding: 0.2rem 0.4rem;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceSunken};
  color: ${({ theme }) => theme.color.text.default};
  font: inherit;
  white-space: nowrap;
`;

const ShortcutGesture = styled.span`
  display: inline-flex;
  align-items: center;
  justify-self: start;
  gap: 0.4rem;
  color: ${({ theme }) => theme.color.status.info};
`;

export interface AnnotationStudioHelpActionProps {
  readonly locale: AnnotationStudioLocale;
}

export function AnnotationStudioHelpAction({ locale }: AnnotationStudioHelpActionProps) {
  const helpPopup = useAnchoredPopupAnchor();
  const helpTooltip = useTooltipController({ suppressed: helpPopup.open });
  const shortcutModifier = resolveHistoryShortcutModifier();
  const helpLabel = annotationStudioText("studioHelp", locale);

  return (
    <>
      <Tooltip
        content={helpLabel}
        placement="bottom"
        controller={helpTooltip}
        describeTrigger={false}
      >
        <IconButton
          {...helpTooltip.triggerProps}
          data-studio-help-action=""
          size="sm"
          variant="ghost"
          border={false}
          selected={helpPopup.open}
          aria-label={helpLabel}
          aria-haspopup="dialog"
          aria-expanded={helpPopup.open}
          onClick={(event) => {
            if (helpPopup.open) {
              helpPopup.close();
              return;
            }
            helpPopup.openFromElement(event);
          }}
        >
          <InfoIcon />
        </IconButton>
      </Tooltip>
      <AnchoredPopup
        open={helpPopup.open}
        anchor={helpPopup.anchor}
        onClose={helpPopup.close}
        placement="bottom-end"
        width="21rem"
        maxHeight="min(28rem, calc(100vh - 1rem))"
        returnFocus
        aria-label={helpLabel}
      >
        <AnchoredPopup.Header divider={false}>
          <HelpTitle variant={defaultTextVariants["caption-bold"]}>{helpLabel}</HelpTitle>
        </AnchoredPopup.Header>
        <AnchoredPopup.Body padding="md">
          <HelpSections>
            <HelpSection>
              <HelpSectionTitle variant={defaultTextVariants["caption-bold"]}>
                {annotationStudioText("playbackTimelineShortcuts", locale)}
              </HelpSectionTitle>
              <HelpList>
                <HelpItem>
                  <ShortcutKey>Space</ShortcutKey>
                  <span>{annotationStudioText("togglePlaybackShortcut", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>← / →</ShortcutKey>
                  <span>{annotationStudioText("movePlayheadShortcut", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>Shift + C</ShortcutKey>
                  <span>{annotationStudioText("captureOrEditShortcut", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>Delete</ShortcutKey>
                  <span>{annotationStudioText("removePlayheadFrameShortcut", locale)}</span>
                </HelpItem>
              </HelpList>
            </HelpSection>
            <HelpSection>
              <HelpSectionTitle variant={defaultTextVariants["caption-bold"]}>
                {annotationStudioText("annotationStatus", locale)}
              </HelpSectionTitle>
              <HelpList>
                <HelpItem>
                  <ShortcutKey>{shortcutModifier} + S</ShortcutKey>
                  <span>{annotationStudioText("saveCameraShortcut", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>1–9</ShortcutKey>
                  <span>{annotationStudioText("selectSlotShortcut", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>0</ShortcutKey>
                  <span>{annotationStudioText("toggleSlotDisplayShortcut", locale)}</span>
                </HelpItem>
                <HelpItem data-annotation-shortcut="include-point">
                  <ShortcutGesture>
                    <ShortcutKey>{annotationStudioText("leftClick", locale)}</ShortcutKey>
                    <AnnotationPointMarkerGlyph kind="include" variant="control" />
                  </ShortcutGesture>
                  <span>{annotationStudioText("addPositivePoint", locale)}</span>
                </HelpItem>
                <HelpItem data-annotation-shortcut="exclude-point">
                  <ShortcutGesture>
                    <ShortcutKey>{annotationStudioText("rightClick", locale)}</ShortcutKey>
                    <AnnotationPointMarkerGlyph kind="exclude" variant="control" />
                  </ShortcutGesture>
                  <span>{annotationStudioText("addNegativePoint", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>{annotationStudioText("markerClick", locale)}</ShortcutKey>
                  <span>{annotationStudioText("removeExistingPoint", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>{shortcutModifier} + Z</ShortcutKey>
                  <span>{annotationStudioText("undoAnnotation", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>{shortcutModifier} + Shift + Z</ShortcutKey>
                  <span>{annotationStudioText("redoAnnotation", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>{annotationStudioText("controlDrag", locale)}</ShortcutKey>
                  <span>{annotationStudioText("panFrame", locale)}</span>
                </HelpItem>
                <HelpItem>
                  <ShortcutKey>{annotationStudioText("controlWheel", locale)}</ShortcutKey>
                  <span>{annotationStudioText("zoomAtPointer", locale)}</span>
                </HelpItem>
              </HelpList>
            </HelpSection>
          </HelpSections>
        </AnchoredPopup.Body>
      </AnchoredPopup>
    </>
  );
}
